import { MfaMethod } from '@fsp-lib/shared/enums';
import { MfaStatus, RefreshTokenResponse } from '@fsp-lib/shared/models';
import { DateTime } from 'luxon';

interface FspApp {
  globalUserId: number;
  lastRefresh?: string;
  refreshToken?: string;
  refreshTokenExpiresAt?: string;
  sessionExp: string;
  operatorId?: number;
  token: string;
}

export interface FspAuth {
  authenticatorMfaEnabled: boolean;
  backupCodesMfaEnabled: boolean;
  defaultMfaMethod: MfaMethod;
  displayMfaSetup: boolean;
  email: string;
  emailMfaEnabled: boolean;
  forceMfaSetup: boolean;
  globalUserId: number;
  gracePeriodDays: number;
  hubSpotUserToken: string;
  mfaEnabled: boolean;
  mfaToken: string;
  operatorId: number;
  returnUrl: string;
}

export function buildMfaStatusFromAuthCookie(authCookie: FspAuth): MfaStatus {
  return {
    emailAddress: authCookie.email,
    emailEnabled: authCookie.emailMfaEnabled,
    authenticatorEnabled: authCookie.authenticatorMfaEnabled,
    defaultMfaMethod: authCookie.defaultMfaMethod,
    backupCodesExist: authCookie.backupCodesMfaEnabled,
    mfaEnabled: authCookie.mfaEnabled,
    mfaRequired: authCookie.forceMfaSetup,
  };
}

export function getCookieObject(cookieName: string) {
  const cookieString = getCookieString(cookieName);
  if (cookieString) {
    return JSON.parse(cookieString);
  }
  return null;
}

export function getCookieString(cookieName: string): string | null {
  cookieName += '=';
  const decodedCookie = decodeURIComponent(document.cookie);
  const ca = decodedCookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(cookieName) === 0) {
      return c.substring(cookieName.length, c.length);
    }
  }
  return null;
}

export function setCookie(
  cookieName: string,
  cookieValue: string,
  daysUntilExpiration = 2,
  allSubDomains = false,
) {
  let domain = '';
  if (allSubDomains) {
    const domains = document.location.hostname.match(
      /[a-z0-9][a-z0-9-]+\.[a-z.]{2,6}$/i,
    );
    domain = domains && domains[0] ? '; domain=.' + domains[0] : '';
  }
  const expirationDateTime = DateTime.utc().plus({ days: daysUntilExpiration });
  const expires = 'expires=' + expirationDateTime.toHTTP();
  document.cookie = `${cookieName}=${encodeURIComponent(
    cookieValue,
  )};${expires};path=/${domain}`;
}

export function getFspAppCookie(): FspApp {
  const name = 'FspApp';
  const cookieObject = getCookieObject(name);
  if (cookieObject) {
    return cookieObject as FspApp;
  }
  return {
    globalUserId: null,
    sessionExp: null,
    operatorId: null,
    token: null,
  };
}

export function getFspAuthCookie(): FspAuth {
  const name = 'FspAuth';
  const cookieObject = getCookieObject(name);
  if (cookieObject) {
    return cookieObject as FspAuth;
  }
  return {
    authenticatorMfaEnabled: false,
    backupCodesMfaEnabled: false,
    defaultMfaMethod: MfaMethod.None,
    displayMfaSetup: false,
    email: null,
    emailMfaEnabled: false,
    forceMfaSetup: false,
    globalUserId: null,
    gracePeriodDays: null,
    hubSpotUserToken: null,
    mfaEnabled: false,
    mfaToken: null,
    operatorId: null,
    returnUrl: null,
  };
}

export function deleteCookie(cookieName: string, allSubDomains = false) {
  // Delete cookie by setting expiration to sometime in the past. Value isn't needed.
  setCookie(cookieName, '', -1, allSubDomains);
}

export const logoutAndRedirectToLogin = function (
  operatorId?: number,
  eventType = 'inactive',
) {
  let urlToRedirectTo = '/Account/Login';
  if (operatorId != null) {
    urlToRedirectTo += `/${operatorId}`;
  }
  urlToRedirectTo += `?event=${eventType}&returnUrl=${encodeURIComponent(
    window.location.href,
  )}`;
  deleteCookie('FspApp', true);
  window.location.replace(urlToRedirectTo);
};

export function readRawCookie(name: string) {
  const nameEq = name + '=';
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEq) === 0) return c.substring(nameEq.length, c.length);
  }
  return null;
}

export function setupFspSession(
  token: string,
  operatorId: number,
  globalUserId?: number,
  sessionExp?: string,
  refreshToken?: string,
  refreshTokenExpiresAt?: string,
): void {
  if (!sessionExp) {
    const expirationDateTime = DateTime.utc().plus({ days: 1 });
    sessionExp = expirationDateTime.toISO();
  }
  const cookieObj = {
    refreshToken,
    sessionExp,
    token: token,
    lastRefresh: DateTime.utc().toISO(),
    refreshTokenExpiresAt,
  };
  if (operatorId) {
    cookieObj['operatorId'] = operatorId;
  }
  if (globalUserId) {
    cookieObj['globalUserId'] = globalUserId;
  }
  setCookie('FspApp', JSON.stringify(cookieObj), 2, true);
}

export function setupFspSkipToken(skipToken?: string): void {
  if (skipToken) {
    setCookie('FspSkipMfa', skipToken, 30, true);
  }
}

export function updateFspSessionOperatorId(operatorId?: number): void {
  const { sessionExp, token, globalUserId, refreshToken } = getFspAppCookie();
  setupFspSession(token, operatorId, globalUserId, sessionExp, refreshToken);
}

export function updateFspSessionToken(
  refreshTokenResponse: RefreshTokenResponse,
): void {
  const { globalUserId, operatorId } = getFspAppCookie();
  setupFspSession(
    refreshTokenResponse.token,
    operatorId,
    globalUserId,
    refreshTokenResponse.expiresAt,
    refreshTokenResponse.refreshToken,
    refreshTokenResponse.refreshTokenExpiresAt,
  );
}

export function isRefreshTokenExpired(): boolean {
  const { refreshTokenExpiresAt } = getFspAppCookie();
  const refreshTokenExpiresAtDate = refreshTokenExpiresAt
    ? DateTime.fromISO(refreshTokenExpiresAt, { zone: 'utc' })
    : DateTime.utc().minus({ days: 1 });
  const datediff = refreshTokenExpiresAtDate.diffNow().milliseconds;
  return datediff < 0;
}

export function isSessionTokenExpired(): boolean {
  const { sessionExp } = getFspAppCookie();
  const sessionExpDate = sessionExp
    ? DateTime.fromISO(sessionExp, { zone: 'utc' })
    : DateTime.utc().minus({ days: 1 });
  const datediff = sessionExpDate.diffNow().milliseconds;
  return datediff < 0;
}
